import { clsx } from '../../../ds/clsx';
import type { ThreadAttachment, ThreadItem } from '../types';
import { TicketAvatar } from '../ui/TicketAvatar';
import { TicketPill } from '../ui/TicketPill';
import styles from './detail.module.css';

/**
 * The thread card of Backoffice 05–08: messages (staff avatar dark, customer light), internal notes on
 * `--warning-bg` with the pill „interne Notiz", and the dividers „Gelöst von …" (hairline) and
 * „Wiedereröffnet durch neue Antwort · …" (accent). Attachments open through `GET /api/files/:id`.
 */
export function Thread({ items }: { items: ThreadItem[] }) {
  return (
    <section
      className={clsx(styles.card, styles.thread, items.length <= 1 && styles.threadSingle)}
      aria-label="Verlauf"
    >
      <ol className={styles.threadList}>
        {items.map((item, index) =>
          item.kind === 'divider' ? (
            <li
              key={`divider-${index}`}
              className={clsx(styles.divider, item.tone === 'accent' && styles.dividerAccent)}
            >
              <span className={styles.dividerLine} aria-hidden="true" />
              <span className={styles.dividerLabel}>{item.label}</span>
              <span className={styles.dividerLine} aria-hidden="true" />
            </li>
          ) : (
            <li key={item.id} className={clsx(styles.message, item.internal && styles.note)}>
              <TicketAvatar
                initials={item.sender.initials}
                size={32}
                tone={item.sender.staff ? 'dark' : 'light'}
              />
              <div className={styles.messageBody}>
                <div className={styles.messageHead}>
                  <span className={styles.sender}>{item.sender.name}</span>
                  <span className={styles.at}>{item.atLabel}</span>
                  {item.internal ? <TicketPill label="interne Notiz" tone="warning" /> : null}
                </div>
                <div className={styles.messageText}>{item.text}</div>
                {item.attachments.length > 0 ? (
                  <div className={styles.attachments}>
                    {item.attachments.map((attachment) => (
                      <AttachmentChip key={attachment.fileId} attachment={attachment} />
                    ))}
                  </div>
                ) : null}
              </div>
            </li>
          ),
        )}
      </ol>
    </section>
  );
}

function AttachmentChip({ attachment }: { attachment: ThreadAttachment }) {
  return (
    <a
      className={styles.fileChip}
      href={`/api/files/${attachment.fileId}`}
      target="_blank"
      rel="noreferrer"
      aria-label={`${attachment.name} öffnen`}
    >
      <span className={styles.fileExt} aria-hidden="true">
        {attachment.ext}
      </span>
      {attachment.name}
    </a>
  );
}
